/* Opus Member Events. Shared by the Member Events page, event pages and the "Events: upcoming" section.
   1. Live details: start times and seats left from the Experiences app, for every event card
      ([data-ev-card]) and event page ticket ([data-ev-live]), loaded as they scroll into view.
   2. Hides cards dated before today (event timezone) in case a page was cached overnight,
      and refreshes the Today / Tomorrow / This week flags.
   3. Member Events page only: Where / Type chips filter instantly and keep ?location= / ?type= in the URL.
   4. Event materials: "Add all to cart" adds every single-option product at once.
   Safe to include more than once on a page. */
(() => {
  if (window.opusEvents) return;
  window.opusEvents = true;

  const list = document.querySelector('[data-ev-list]');
  const tz = (list && list.dataset.tz) || 'America/Vancouver';
  const live = !list || list.dataset.live !== 'false';
  const lowSeats = Number((list && list.dataset.lowSeats) || 6);
  const goingMin = Number((list && list.dataset.goingMin) || 20);

  /* ---------- Dates in the event timezone ---------- */
  const ymd = (date) => {
    const parts = new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date);
    const get = (t) => parts.find((p) => p.type === t).value;
    return Number(get('year') + get('month') + get('day'));
  };
  const today = ymd(new Date());
  const dayMs = 86400000;
  const tomorrow = ymd(new Date(Date.now() + dayMs));
  const weekEnd = ymd(new Date(Date.now() + 6 * dayMs));

  const allCards = [...document.querySelectorAll('[data-ev-card]')];
  allCards.forEach((card) => {
    const date = Number(card.dataset.date);
    if (date < today) {
      card.dataset.past = 'true';
      card.hidden = true;
      return;
    }
    const flag = card.querySelector('[data-ev-flag]');
    if (!flag) return;
    const label = date === today ? 'Today' : date === tomorrow ? 'Tomorrow' : date <= weekEnd ? 'This week' : '';
    flag.textContent = label;
    flag.hidden = !label;
  });

  /* ---------- Live times and seats (Experiences app) ---------- */
  const API = 'https://widgets.production.experiencesapp.services/api/book-now?productId=';
  const CACHE_MS = 5 * 60 * 1000;
  const queue = [];
  const seen = new WeakSet();
  let active = 0;

  const fmtTime = (t) => (t || '').replace(/^(\d+):00/, '$1').replace(/(AM|PM)$/i, (m) => m.toLowerCase());

  const render = (el, exp) => {
    const date = Number(el.dataset.date);
    let slots = [...((exp && exp.timeslots) || [])].sort((a, b) => new Date(a.date) - new Date(b.date));
    const sameDay = slots.filter((s) => ymd(new Date(s.date)) === date);
    if (sameDay.length) slots = sameDay;
    if (!slots.length) return;

    const first = slots[0];
    const last = slots[slots.length - 1];
    const range = first.startTime ? `${fmtTime(first.startTime)}–${fmtTime(last.endTime || first.endTime)}` : '';
    const sessions = slots.length > 1 ? `${slots.length} sessions` : '';

    const timeEl = el.querySelector('[data-ev-time]');
    if (timeEl && range) timeEl.textContent = ` · ${range}${sessions ? ` · ${sessions}` : ''}`;
    const fullEl = el.querySelector('[data-ev-time-full]');
    if (fullEl && range) {
      const len = fullEl.textContent.trim();
      fullEl.textContent = [range, sessions || (/\d/.test(len) ? len : '')].filter(Boolean).join(' · ');
    }

    // Event page: Google Calendar link from the first session's real start time
    const cal = el.querySelector('[data-ev-cal]');
    if (cal && first.date) {
      const start = new Date(first.date);
      const lastStart = new Date(last.date);
      const hrs = Number(last.duration) || Number(el.dataset.duration || 0) / 60 || 1;
      const end = new Date(lastStart.getTime() + hrs * 3600000);
      const fmt = (d) => d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      const q = new URLSearchParams({
        action: 'TEMPLATE',
        text: el.dataset.title || document.title,
        dates: `${fmt(start)}/${fmt(end)}`,
        details: `${location.origin}${location.pathname}`,
        location: el.dataset.place || '',
      });
      cal.href = `https://calendar.google.com/calendar/render?${q}`;
      cal.hidden = false;
    }

    const seatsEl = el.querySelector('[data-ev-seats]');
    if (!seatsEl) return;
    const base = seatsEl.classList.contains('ev-facts__seats') ? 'ev-facts__seats' : 'ev-card__seats';
    const total = slots.reduce((n, s) => n + (Number(s.total) || 0), 0);
    const booked = slots.reduce((n, s) => n + (Number(s.booked) || 0), 0);
    const left = total - booked;
    const unlimited = el.dataset.mode === 'online' || total >= 500;

    if (unlimited) {
      if (booked >= goingMin) {
        seatsEl.textContent = `${booked} artists registered`;
        seatsEl.className = `${base} is-going`;
        seatsEl.hidden = false;
      }
    } else if (total > 0 && left <= 0) {
      el.classList.add('is-full');
      seatsEl.textContent = 'Fully booked';
      seatsEl.className = `${base} is-low`;
      seatsEl.hidden = false;
      const cta = el.querySelector('[data-ev-cta]');
      if (cta) cta.textContent = 'See details';
    } else if (total > 0 && left <= lowSeats) {
      seatsEl.textContent = `Only ${left} ${left === 1 ? 'seat' : 'seats'} left`;
      seatsEl.className = `${base} is-low`;
      seatsEl.hidden = false;
    }
  };

  const load = async (el) => {
    const id = el.dataset.pid;
    const key = `ev:${id}`;
    try {
      const cached = JSON.parse(sessionStorage.getItem(key) || 'null');
      if (cached && Date.now() - cached.t < CACHE_MS) return render(el, cached.e);
    } catch (e) { /* storage unavailable */ }
    const res = await fetch(API + id, { credentials: 'omit' });
    if (!res.ok) return;
    const json = await res.json();
    const exp = json && json.experience;
    if (!exp) return;
    const slim = { timeslots: (exp.timeslots || []).map(({ date, startTime, endTime, total, booked, duration }) => ({ date, startTime, endTime, total, booked, duration })) };
    try { sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), e: slim })); } catch (e) { /* full or blocked */ }
    render(el, slim);
  };

  const pump = () => {
    while (active < 4 && queue.length) {
      const el = queue.shift();
      active += 1;
      load(el).catch(() => {}).finally(() => { active -= 1; pump(); });
    }
  };

  const io = live && 'IntersectionObserver' in window
    ? new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting || seen.has(e.target)) return;
          seen.add(e.target);
          io.unobserve(e.target);
          queue.push(e.target);
        });
        pump();
      }, { rootMargin: '400px 0px' })
    : null;

  const queueVisible = () => {
    if (!io) return;
    document.querySelectorAll('[data-ev-card], [data-ev-live]').forEach((el) => {
      if (!el.hidden && !el.dataset.past && !seen.has(el)) io.observe(el);
    });
  };

  /* ---------- Member Events page filters ---------- */
  if (list) {
    const cards = [...list.querySelectorAll('[data-ev-card]')];
    const months = [...list.querySelectorAll('[data-ev-month]')];
    const chips = [...list.querySelectorAll('[data-ev-chip]')];
    const storeSelect = list.querySelector('[data-ev-select="loc"]');
    const status = list.querySelector('[data-ev-status]');
    const empty = list.querySelector('[data-ev-empty]');

    const state = { loc: 'all', type: 'all' };
    const params = new URLSearchParams(location.search);
    const known = (group, value) => chips.some((c) => c.dataset.evChip === group && c.dataset.value === value)
      || (group === 'loc' && storeSelect && [...storeSelect.options].some((o) => o.value && o.value === value));
    const fromUrl = (group, key) => {
      const v = (params.get(key) || '').toLowerCase().replace(/[^a-z0-9-]/g, '');
      const alias = { 'in-store': 'instore', store: 'instore' }[v] || v;
      if (alias && known(group, alias)) state[group] = alias;
    };
    fromUrl('loc', 'location');
    fromUrl('type', 'type');

    const matches = (card) => {
      if (card.dataset.past) return false;
      const { loc, type } = state;
      if (loc === 'instore' && card.dataset.mode !== 'instore') return false;
      if (loc === 'online' && card.dataset.mode !== 'online') return false;
      if (!['all', 'instore', 'online'].includes(loc) && card.dataset.loc !== loc) return false;
      if (type === 'free' && card.dataset.kind !== 'free') return false;
      if (type === 'credit' && card.dataset.kind !== 'credit') return false;
      if (type === 'workshop' && card.dataset.workshop !== 'true') return false;
      return true;
    };

    const labelFor = (group) => {
      const chip = chips.find((c) => c.dataset.evChip === group && c.dataset.value === state[group]);
      if (!chip) {
        const opt = group === 'loc' && storeSelect && [...storeSelect.options].find((o) => o.value === state.loc);
        return opt ? opt.textContent.replace(/\s*\(\d+\)\s*$/, '').trim() : '';
      }
      const clone = chip.cloneNode(true);
      clone.querySelectorAll('.ev-chip__n, svg').forEach((n) => n.remove());
      return clone.textContent.trim();
    };

    const apply = (updateUrl) => {
      let shown = 0;
      cards.forEach((card) => {
        const ok = matches(card);
        card.hidden = !ok;
        if (ok) shown += 1;
      });
      months.forEach((m) => {
        m.hidden = !m.querySelector('[data-ev-card]:not([hidden])');
      });
      chips.forEach((c) => c.setAttribute('aria-pressed', String(state[c.dataset.evChip] === c.dataset.value)));
      if (storeSelect) {
        const isStore = [...storeSelect.options].some((o) => o.value && o.value === state.loc);
        storeSelect.value = isStore ? state.loc : '';
        storeSelect.closest('.ev-store-select').classList.toggle('is-active', isStore);
      }
      if (empty) empty.hidden = shown > 0;

      if (status && cards.length) {
        const bits = [];
        if (state.loc !== 'all') bits.push(state.loc === 'online' ? 'online' : state.loc === 'instore' ? 'in-store' : `at ${labelFor('loc')}`);
        if (state.type !== 'all') bits.push(`· ${labelFor('type').toLowerCase()}`);
        status.textContent = `${shown} upcoming ${shown === 1 ? 'event' : 'events'}${bits.length ? ' ' + bits.join(' ') : ''}`;
      }

      if (updateUrl) {
        const url = new URL(location.href);
        state.loc === 'all' ? url.searchParams.delete('location') : url.searchParams.set('location', state.loc);
        state.type === 'all' ? url.searchParams.delete('type') : url.searchParams.set('type', state.type);
        history.replaceState(history.state, '', url);
      }
      queueVisible();
    };

    chips.forEach((chip) => {
      chip.addEventListener('click', () => {
        const group = chip.dataset.evChip;
        state[group] = state[group] === chip.dataset.value && chip.dataset.value !== 'all' ? 'all' : chip.dataset.value;
        apply(true);
      });
    });
    storeSelect?.addEventListener('change', () => {
      state.loc = storeSelect.value || 'all';
      apply(true);
    });
    list.querySelector('[data-ev-reset]')?.addEventListener('click', () => {
      state.loc = 'all';
      state.type = 'all';
      apply(true);
    });

    // Counts no longer add up once past events drop out (cached pages)
    if (cards.some((c) => c.dataset.past)) {
      chips.forEach((chip) => chip.querySelector('.ev-chip__n')?.remove());
    }

    apply(state.loc !== 'all' || state.type !== 'all');
  } else {
    queueVisible();
  }

  /* ---------- Event page: booking widget and sticky bar ---------- */
  const facts = document.querySelector('.ev-facts[data-ev-live]');
  if (facts) {
    // The Experiences widget renders after load and re-renders when a session is picked.
    // Give its "Select" buttons a clearer label each time it does.
    const free = Number(facts.dataset.price || 0) === 0;
    const LABELS = ['Select', 'Reserve', 'Reserve free spot', 'Reserve my seat', 'Reserve this session', 'Add to cart'];
    const relabel = (root) => {
      const buttons = root.querySelectorAll('.expapp-timeslot-button');
      // Free spots skip the cart and go to checkout; paid seats go in the cart like any product.
      // One session gets the full label; with several, a short one keeps each row on one line.
      const label = !free ? 'Add to cart' : buttons.length > 1 ? 'Reserve' : 'Reserve free spot';
      buttons.forEach((b) => {
        if (b.dataset.evBusy) return;
        if (LABELS.includes(b.textContent.trim()) && b.textContent.trim() !== label) b.textContent = label;
      });
    };

    /* Direct booking: one spot straight from the page, without the app's popup.
       The popup only adds the ticket to the cart with these line properties; the app turns the
       paid order into a booking from _Timeslot. Events that need the popup (several ticket types,
       attendee questions, group or pay-later bookings, required products) still get it, and so does
       any click before the event data has loaded or when adding fails. */
    let exp = null;
    fetch(API + facts.dataset.pid, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { exp = (j && j.experience) || null; })
      .catch(() => {});
    const direct = () => exp && exp.paymentType === 'prepay' && exp.allowSingleBooking !== false
      && !exp.customForm && (exp.variants || []).length === 1 && !exp.variants[0].productCollectionRequired;

    // The widget's rows carry no ids, so match a row to its session by date and start time
    const MONTHS = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ');
    const rowKey = (row) => {
      const d = (row.querySelector('.expapp-timeslot-date') || {}).textContent || '';
      const t = (row.querySelector('.expapp-timeslot-time') || {}).textContent || '';
      const dm = d.match(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\s+(\d{1,2})\D+(\d{4})/);
      const tm = t.match(/(\d{1,2}):(\d{2})\s*([AP]M)/i);
      return dm && tm ? `${dm[3]}-${MONTHS.indexOf(dm[1])}-${+dm[2]} ${+tm[1]}:${tm[2]}${tm[3].toUpperCase()}` : '';
    };
    const slotKey = (s) => {
      const p = {};
      new Intl.DateTimeFormat('en-US', { timeZone: exp.timezone || tz, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
        .formatToParts(new Date(s.date)).forEach((x) => { p[x.type] = x.value; });
      return `${p.year}-${p.month - 1}-${p.day} ${p.hour}:${p.minute}${(p.dayPeriod || '').toUpperCase()}`;
    };
    const findSlot = (button) => {
      const slots = (exp.timeslots || []).filter((s) => !(Number(s.total) > 0 && Number(s.booked) >= Number(s.total)));
      const row = button.closest('.expapp-timeslot');
      const key = row && rowKey(row);
      const match = key && slots.find((s) => slotKey(s) === key);
      if (match) return match;
      const rows = document.querySelectorAll('[data-expapp-v2] .expapp-timeslot-button');
      return rows.length === 1 && slots.length === 1 ? slots[0] : null;
    };

    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    // Tickets are one per person: after booking, point people at "Invite a friend" (assets/ev-invite.js)
    const inviteHtml = () => {
      const src = facts.querySelector('[data-ev-invite]');
      if (!src) return '';
      const b = src.cloneNode(false);
      b.className = 'ev-invite ev-invite--inline';
      b.textContent = 'send them an invite';
      return `<br>Bringing a friend? Spots are one per person, so ${b.outerHTML} to book their own.`;
    };
    const note = (button, html) => {
      const box = button.closest('[data-expapp-v2]') || button.parentElement;
      let el = box.parentElement.querySelector('.ev-book-note');
      if (!el) {
        el = document.createElement('p');
        el.className = 'ev-book-note';
        el.setAttribute('role', 'status');
        box.after(el);
      }
      el.innerHTML = html;
    };

    const book = async (button, slot) => {
      const variantId = exp.variants[0].shopifyVariantId;
      const ts = String(Math.round((slot.timestamp || new Date(slot.date).getTime()) / 1000));
      const label = button.textContent;
      let leaving = false;
      button.dataset.evBusy = 'true';
      button.setAttribute('aria-busy', 'true');
      button.textContent = free ? 'Reserving…' : 'Adding…';
      try {
        // One spot per person: if this session is already in the cart, don't add a second
        const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
        const inCart = (cart.items || []).some((i) => i.variant_id === variantId && (i.properties || {})._Timeslot === ts);
        if (inCart) {
          note(button, (free
            ? `Your spot is already in your cart. <a href="/checkout">Finish reserving</a>`
            : `This event is already in your cart. <a href="${cartUrl}">View cart</a>`) + inviteHtml());
          return;
        }

        const drawer = free ? null : document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const body = {
          id: variantId,
          quantity: 1,
          properties: { When: slot.when || slot.startDateTime || '', BookingType: 'REGULAR', _Timeslot: ts },
        };
        if (drawer && typeof drawer.getSectionsToRender === 'function') {
          body.sections = drawer.getSectionsToRender().map((s) => s.id);
          body.sections_url = location.pathname;
        }
        const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart');

        // Free: nothing else to buy, so go straight to checkout, where the spot is confirmed
        if (free) {
          leaving = true;
          location.href = '/checkout';
          return;
        }
        if (drawer && json.sections) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(json);
        } else {
          leaving = true;
          location.href = cartUrl;
          return;
        }
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'event-booking', productVariantId: variantId, cartData: json });
        }
        note(button, `Added to your cart. Your seat is booked once you check out. <a href="${cartUrl}">View cart</a>${inviteHtml()}`);
      } catch (err) {
        // Something went wrong: hand this click to the app's own popup instead
        button.dataset.evPopup = 'true';
        button.textContent = label;
        delete button.dataset.evBusy;
        button.click();
        return;
      } finally {
        if (button.dataset.evBusy && !leaving) {
          button.removeAttribute('aria-busy');
          delete button.dataset.evBusy;
          button.textContent = label;
        }
      }
    };

    document.addEventListener('click', (ev) => {
      const button = ev.target.closest && ev.target.closest('.expapp-timeslot-button');
      if (!button || !button.closest('[data-expapp-v2]') || button.closest('dialog')) return;
      if (button.dataset.evPopup) { delete button.dataset.evPopup; return; }
      if (button.dataset.evBusy) { ev.preventDefault(); ev.stopImmediatePropagation(); return; }
      const slot = direct() && findSlot(button);
      if (!slot) return;
      ev.preventDefault();
      ev.stopImmediatePropagation();
      book(button, slot);
    }, true);
    let watchWidget = () => {
      document.querySelectorAll('[data-expapp-v2]').forEach((w) => {
        if (w.dataset.evWatched) return;
        w.dataset.evWatched = 'true';
        relabel(w);
        new MutationObserver(() => relabel(w)).observe(w, { childList: true, subtree: true });
      });
    };
    // Move the widget into the ticket, so date, price and booking are one card
    const slot = facts.querySelector('[data-ev-book-slot]');
    const adopt = () => {
      if (!slot) return;
      const mount = document.querySelector('[data-expapp-v2]');
      // Wait until the app has drawn the widget; moving its empty placeholder stops it rendering
      if (!mount || slot.contains(mount) || !mount.querySelector('.expapp')) return;
      slot.appendChild(mount);
      slot.hidden = false;
      facts.classList.add('has-booking');
    };
    const watchAll = watchWidget;
    watchWidget = () => { adopt(); watchAll(); };
    watchWidget();
    const pageWatch = new MutationObserver(() => {
      watchWidget();
      if (slot && slot.contains(document.querySelector('[data-expapp-v2]'))) pageWatch.disconnect();
    });
    pageWatch.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => pageWatch.disconnect(), 15000);

    // Phones: a slim bar with the price and a Reserve button once the booking area is off screen
    const sticky = document.querySelector('[data-ev-sticky]');
    const target = document.getElementById('book') || facts;
    if (sticky && target && 'IntersectionObserver' in window) {
      new IntersectionObserver(([e]) => { sticky.hidden = e.isIntersecting; }).observe(target);
      sticky.querySelector('a')?.addEventListener('click', (ev) => {
        ev.preventDefault();
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }

  /* ---------- Event materials: add all to cart ---------- */
  document.querySelectorAll('[data-ev-kit]').forEach((btn) => {
    const wrap = btn.closest('.ev-materials');
    const statusEl = wrap && wrap.querySelector('[data-ev-kit-status]');
    const labelEl = btn.querySelector('[data-ev-kit-label]');
    const original = labelEl ? labelEl.textContent : '';

    btn.addEventListener('click', async () => {
      const ids = btn.dataset.evKit.split(',').filter(Boolean);
      if (!ids.length || btn.getAttribute('aria-disabled') === 'true') return;
      btn.setAttribute('aria-disabled', 'true');
      btn.classList.add('loading');
      const drawer = document.querySelector('cart-drawer');
      const body = { items: ids.map((id) => ({ id: Number(id), quantity: 1 })) };
      if (drawer && typeof drawer.getSectionsToRender === 'function') {
        body.sections = drawer.getSectionsToRender().map((s) => s.id);
        body.sections_url = location.pathname;
      } else {
        body.sections = ['cart-icon-bubble'];
      }
      try {
        const res = await fetch(`${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (!res.ok || json.status) throw new Error(json.description || json.message || 'Could not add to cart');

        if (drawer && json.sections) {
          drawer.classList.contains('is-empty') && drawer.classList.remove('is-empty');
          drawer.renderContents(json);
        } else {
          const bubble = document.getElementById('cart-icon-bubble');
          if (bubble && json.sections && json.sections['cart-icon-bubble']) {
            const doc = new DOMParser().parseFromString(json.sections['cart-icon-bubble'], 'text/html');
            const inner = doc.querySelector('.shopify-section');
            if (inner) bubble.innerHTML = inner.innerHTML;
          }
          if (statusEl) {
            statusEl.innerHTML = `Added ${ids.length} items to your cart. <a href="${(window.routes && window.routes.cart_url) || '/cart'}">View cart</a>`;
            statusEl.hidden = false;
          }
        }
        if (labelEl) labelEl.textContent = 'Added to cart';
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'event-materials' });
        }
      } catch (err) {
        if (statusEl) {
          statusEl.textContent = `${err.message}. Try adding items one at a time.`;
          statusEl.hidden = false;
        }
        if (labelEl) labelEl.textContent = original;
      } finally {
        btn.removeAttribute('aria-disabled');
        btn.classList.remove('loading');
        setTimeout(() => { if (labelEl && labelEl.textContent === 'Added to cart') labelEl.textContent = original; }, 4000);
      }
    });
  });
})();
